import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';
import { Card, CardHeader, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { AddressInput } from '../components/AddressInput';
import { userAPI } from '../api/userAPI';
import { regionAPI } from '../api/regionAPI';
import { Avatar } from '../components/Avatar';
import { ImageCropModal } from '../components/ImageCropModal';
import { validatePhone, validateEmail, validateDate } from '../utils/validation';
import { toInputDate } from '../utils/format';

const ALL_ROLES = [
    { value: 'admin', label: 'Admin' },
    { value: 'pastor', label: 'Pastor' },
    { value: 'regional_leader', label: 'Regional Leader' },
    { value: 'regional_assistant', label: 'Regional Assistant' },
    { value: 'intake', label: 'Intake' },
];

const emptyForm = {
    email: '', first_name: '', last_name: '', middle_initial: '', phone: '', roles: ['regional_assistant'], region_id: '',
    address: '', latitude: null, longitude: null, birthdate: '', wedding_anniversary: '',
};

export function LeaderDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const isNew = !id || id === 'new';

    const [form, setForm] = useState({ ...emptyForm });
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [leaderData, setLeaderData] = useState(null);
    const [inviteLink, setInviteLink] = useState(null);
    const photoInputRef = useRef(null);
    const [cropFile, setCropFile] = useState(null);

    const handlePhotoSelect = (e) => {
        const file = e.target.files[0];
        if (file) setCropFile(file);
        e.target.value = '';
    };

    const handleCroppedUpload = async (croppedFile) => {
        setCropFile(null);
        try {
            const res = await userAPI.uploadPhoto(id, croppedFile);
            const photoUrl = res.data?.data?.photo_url;
            if (photoUrl) {
                setLeaderData((prev) => ({ ...prev, photo_url: photoUrl }));
                setSuccess('Photo uploaded');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload photo');
        }
    };

    useEffect(() => {
        regionAPI.getRegions().then((res) => {
            setRegions((res.data.data || {}).items || []);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        userAPI.getUser(id).then((res) => {
            const u = res.data.data || res.data;
            setLeaderData(u);
            setForm({
                email: u.email || '',
                first_name: u.first_name || '',
                last_name: u.last_name || '',
                middle_initial: u.middle_initial || '',
                phone: u.phone || '',
                roles: u.roles || [],
                region_id: u.region_id || '',
                address: u.address || '',
                latitude: u.latitude || null,
                longitude: u.longitude || null,
                birthdate: toInputDate(u.birthdate),
                wedding_anniversary: toInputDate(u.wedding_anniversary),
            });
        }).catch((err) => {
            setError(err.response?.data?.message || 'Failed to load leader');
        }).finally(() => setLoading(false));
    }, [id, isNew]);

    const handleRoleToggle = (roleValue) => {
        setForm((prev) => {
            const roles = prev.roles || [];
            if (roles.includes(roleValue)) {
                return { ...prev, roles: roles.filter((r) => r !== roleValue) };
            }
            return { ...prev, roles: [...roles, roleValue] };
        });
    };

    const validateForm = () => {
        const errors = {};
        if (form.email) {
            const emailErr = validateEmail(form.email);
            if (emailErr) errors.email = emailErr;
        }
        const phoneErr = validatePhone(form.phone);
        if (phoneErr) errors.phone = phoneErr;
        const bdErr = validateDate(form.birthdate, 'Birthdate');
        if (bdErr) errors.birthdate = bdErr;
        const waErr = validateDate(form.wedding_anniversary, 'Wedding anniversary');
        if (waErr) errors.wedding_anniversary = waErr;
        if (!form.roles || form.roles.length === 0) errors.roles = 'At least one role is required';
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;
        setSaving(true);
        try {
            if (isNew) {
                const res = await userAPI.createUser(form);
                const newLeader = res.data?.data;
                const inviteToken = res.data?.invite_token;
                if (inviteToken) {
                    setInviteLink(`${window.location.origin}/signup?token=${inviteToken}`);
                }
                if (newLeader?.id) {
                    setLeaderData(newLeader);
                    navigate(`/users/${newLeader.id}`, { replace: true });
                }
                setSuccess('Leader created. Share the invite link below.');
            } else {
                await userAPI.updateUser(id, form);
                setSuccess('Leader updated');
                setTimeout(() => navigate('/users'), 600);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Operation failed');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this leader?')) return;
        try {
            await userAPI.deleteUser(id);
            navigate('/users');
        } catch (err) {
            setError('Failed to delete leader');
        }
    };

    const handleDemote = async () => {
        if (!leaderData) return;
        if (!window.confirm(`Convert "${leaderData.name}" to a member? Their leader account will be removed.`)) return;
        try {
            await userAPI.demoteToMember(id);
            setSuccess(`${leaderData.name} converted to member successfully.`);
            setTimeout(() => navigate('/users'), 600);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to convert leader to member');
        }
    };

    const handleRegenerateInvite = async () => {
        try {
            const res = await userAPI.regenerateInvite(id);
            const token = res.data?.data?.invite_token;
            if (token) {
                setInviteLink(`${window.location.origin}/signup?token=${token}`);
                setSuccess('Invite link regenerated.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to regenerate invite');
        }
    };

    if (!isAdmin) {
        return (
            <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
                <Card><CardBody><h2>Access Denied</h2><p>Only administrators can manage leaders.</p><Button onClick={() => navigate('/dashboard')}>Back</Button></CardBody></Card>
            </div>
        );
    }

    if (loading) {
        return <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in"><p className="text-center text-slate-500 py-6">Loading...</p></div>;
    }

    return (
        <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
            <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">{isNew ? 'Create Leader' : 'Edit Leader'}</h1>
                    {!isNew && leaderData && <p className="text-sm text-slate-500 mt-0.5">{leaderData.name}</p>}
                </div>
                <div>
                    <Button variant="secondary" onClick={() => navigate('/users')}>Back</Button>
                </div>
            </header>

            <main>
                {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
                {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

                {inviteLink && (
                    <div className="card-base p-4 mb-4">
                        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">Invite Link</h3>
                        <p className="text-sm text-slate-500 mb-2">
                            Share this link with the new leader to complete signup:
                        </p>
                        <div className="flex gap-2 items-center">
                            <input type="text" value={inviteLink} readOnly className="input-field flex-1" />
                            <Button size="sm" onClick={() => { navigator.clipboard.writeText(inviteLink); setSuccess('Invite link copied!'); }}>Copy</Button>
                        </div>
                    </div>
                )}

                {!isNew && leaderData && (
                    <div className="flex items-center gap-4 mb-4">
                        <Avatar name={leaderData.name} photoUrl={leaderData.photo_url} size="xl" />
                        <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhotoSelect} />
                        <Button size="sm" variant="secondary" onClick={() => photoInputRef.current?.click()}>Change Photo</Button>
                    </div>
                )}
                {cropFile && <ImageCropModal file={cropFile} onCrop={handleCroppedUpload} onCancel={() => setCropFile(null)} />}

                <Card>
                    <CardHeader>
                        <h2>{isNew ? 'New Leader' : 'Leader Details'}</h2>
                    </CardHeader>
                    <CardBody>
                        <form className="grid sm:grid-cols-2 gap-4" onSubmit={handleSubmit}>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Email</label>
                                <input className="input-field" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!isNew && leaderData?.status !== 'pending'} placeholder="Optional — linked when leader signs in" />
                                {formErrors.email && <span className="text-red-500 text-xs mt-0.5">{formErrors.email}</span>}
                            </div>
                            <div className="sm:col-span-2 flex gap-3">
                                <div className="flex flex-col gap-1.5 flex-1">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">First Name *</label>
                                    <input className="input-field" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                                </div>
                                <div className="flex flex-col gap-1.5 w-16">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">M.I.</label>
                                    <input className="input-field text-center" maxLength={1} value={form.middle_initial} onChange={(e) => setForm({ ...form, middle_initial: e.target.value })} />
                                </div>
                                <div className="flex flex-col gap-1.5 flex-1">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Last Name *</label>
                                    <input className="input-field" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                                </div>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Phone</label>
                                <input className="input-field" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                                {formErrors.phone && <span className="text-red-500 text-xs mt-0.5">{formErrors.phone}</span>}
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Roles *</label>
                                <div className="flex flex-col gap-2">
                                    {ALL_ROLES.map((r) => (
                                        <label key={r.value} className="flex items-center gap-2 text-sm cursor-pointer text-slate-700 dark:text-slate-300">
                                            <input type="checkbox" checked={(form.roles || []).includes(r.value)} onChange={() => handleRoleToggle(r.value)} />
                                            {r.label}
                                        </label>
                                    ))}
                                </div>
                                {formErrors.roles && <span className="text-red-500 text-xs mt-0.5">{formErrors.roles}</span>}
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Region</label>
                                <select className="input-field" value={form.region_id} onChange={(e) => setForm({ ...form, region_id: e.target.value })}>
                                    <option value="">Select a region</option>
                                    {regions.map((r) => (
                                        <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Address</label>
                                <AddressInput
                                    value={form.address}
                                    onChange={({ address, latitude, longitude }) => setForm({ ...form, address, latitude, longitude })}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Birthdate</label>
                                <input className="input-field" type="date" max={new Date().toISOString().split('T')[0]} value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} />
                                {formErrors.birthdate && <span className="text-red-500 text-xs mt-0.5">{formErrors.birthdate}</span>}
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Wedding Anniversary</label>
                                <input className="input-field" type="date" max={new Date().toISOString().split('T')[0]} value={form.wedding_anniversary} onChange={(e) => setForm({ ...form, wedding_anniversary: e.target.value })} />
                                {formErrors.wedding_anniversary && <span className="text-red-500 text-xs mt-0.5">{formErrors.wedding_anniversary}</span>}
                            </div>
                            <div className="flex gap-3 sm:col-span-2 mt-2">
                                <Button type="submit" disabled={saving}>{saving ? 'Saving...' : (isNew ? 'Create Leader' : 'Update Leader')}</Button>
                                <Button variant="secondary" type="button" onClick={() => navigate('/users')}>Cancel</Button>
                            </div>
                        </form>
                    </CardBody>
                </Card>

                {!isNew && leaderData && (
                    <div className="card-base border-red-200 dark:border-red-800 p-4 mt-6">
                        <h3 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-3">Actions</h3>
                        <div className="flex flex-wrap gap-2">
                            {leaderData.status === 'pending' && (
                                <Button variant="secondary" onClick={handleRegenerateInvite}>Regenerate Invite Link</Button>
                            )}
                            {!(leaderData.roles || []).includes('admin') && !(leaderData.roles || []).includes('pastor') && (
                                <Button variant="secondary" onClick={handleDemote}>Make Member</Button>
                            )}
                            <Button variant="danger" onClick={handleDelete}>Delete</Button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

export default LeaderDetail;
